import * as React from 'react'
import {ReactElement, ReactNode} from 'react'
import {DisplayItemSelectLikeDiv, DisplayItemSelectWrapper} from '../../../../styledComponents/DisplayItem'
import {Box, Breadcrumbs, Menu, MenuItem} from '@mui/material'
import withStyles from '@mui/styles/withStyles'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import styled from 'styled-components'
import {BreakpointValues, Colors, DisplaySizeValue, FontSize, FontWeights} from '../../../../styledComponents/Constants'
import {NatAssocWorkReportRepo} from '../repository/NatAssocWorkReportRepo'
import {NatGetAccountingTermsDetail} from '../repository/NatGetAccountingTerms'
import {
    BusinessInfo,
    NatGetAssocWorkReportPlanResultsDetail,
    ResultFile,
} from '../repository/NatGetAssocWorkReportPlanResults'
import {Nullable} from '../../../../app/TypeHelpers'
import {StyledPageTitle} from '../../../../components/reservations/components/CommonStyle'
import {convertToMoment} from '../../../../components/reservations/helpers/helpers'
import {formatYearAndMonth} from '../../../../formatters/formatMoment'
import {HAVE_OR_NOT, PLAN_EXISTENCE} from '../../../../CodeMaster'
import {RowFlexContainer} from '../../../../styledComponents/FlexComponents'
import {PlainLinkStyledButton} from '../../../../styledComponents/Button'
import {NatGetNextTermReportsDetail} from '../repository/NatGetNextTermReports'
import {NatNextTermReport} from './resident/NatNextTermReport'
import {currentMedia} from '../../../../styledComponents/ResponsiveConvenience'
import NatAssocWorkReportTimeline from './resident/NatAssocWorkReportTimeline'
import ResponsiveDialog from '../../../../components/shared/ResponsiveDialog'
import {NatBusinessDetailDialog} from './resident/NatBusinessDetailDialog'
import {StickyDiv} from '../../../../components/reservations/components/ReservationTimelineView'
import {NatResidentReportFileListDialog} from './NatResidentReportFileListDialog'
import moment from 'moment'
import NatCurrentDateTimeRepo from '../../currentDateTime/repository/NatCurrentDateTimeRepo'
import {Assoc} from '../repository/NatGetAssocInfos'
import {WithWidth, WithWidthProps} from '../../../../components/shared/forms/hoc/withMediaQuery'
import {
    HeadingAreaMessageComponentForResident
} from '../../../../components/shared/pageBaseComponents/HeadingAreaMessageComponentForResident'
import {Messages} from '../../../../Messages'
import {NatResidentMergedReportFileDialog} from './NatResidentMergedReportFileDialog'
import {faDownload} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'

interface Props {
    condominiumCd: string
    assocCd: string
    assocWorkReportRepo: NatAssocWorkReportRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
}

interface State {
    assocs: Assoc[]
    currentMoment: Nullable<moment.Moment>
    accountingTermsDetail: NatGetAccountingTermsDetail
    assocWorkReportPlanResultsDetail: NatGetAssocWorkReportPlanResultsDetail
    fileSizeByYearMonthMap: Map<string, number>
    nextTermReports: NatGetNextTermReportsDetail
    accountingListOpen: boolean
    accountingTermSelectBox: Nullable<HTMLElement>
    selectedAccountingKey: string | null
    selectMenuList: SelectMenu[]
    confirmationBusinessInfo: Nullable<BusinessInfo>
    confirmationYearMonthInfo:
        Nullable<{ targetYearMonth: string, totalFileSizeKB: number }>
    confirmationResultInfo: Nullable<{ businessName: string, resultFiles: ResultFile[] }>
    dialogStatus: Nullable<DialogContentType>
    isLoadCompleted: boolean
}

interface SelectMenu {
    label: string
    additionalLabel: string
    value: string | null
}

type DialogContentType = 'business' | 'yearMonth' | 'resultFileList'

class NatResidentAssocWorkReportPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet & WithWidthProps, State> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet & WithWidthProps) {
        super(props)

        this.state = {
            assocs: [],
            currentMoment: null,
            accountingTermsDetail: {
                accountingTermInfo: [],
                nextTermReportFlag: '',
            },
            assocWorkReportPlanResultsDetail: {
                planResults: [],
            },
            fileSizeByYearMonthMap: new Map<string, number>(),
            nextTermReports: {
                resultFiles: [],
            },
            accountingListOpen: false,
            accountingTermSelectBox: null,
            selectedAccountingKey: null,
            selectMenuList: [],
            confirmationBusinessInfo: null,
            confirmationYearMonthInfo: null,
            confirmationResultInfo: null,
            dialogStatus: null,
            isLoadCompleted: false,
        }

        this.createFileSizeByYearMonthMap = this.createFileSizeByYearMonthMap.bind(this)
        this.handleAccountingListOpen = this.handleAccountingListOpen.bind(this)
        this.handleAccountingListClose = this.handleAccountingListClose.bind(this)
        this.handleAccountingTermChange = this.handleAccountingTermChange.bind(this)
        this.handleBusinessNameClick = this.handleBusinessNameClick.bind(this)
        this.handleDownloadIconClick = this.handleDownloadIconClick.bind(this)
        this.handleResultClick = this.handleResultClick.bind(this)
        this.handleDialogClose = this.handleDialogClose.bind(this)
        this.handleMergedFileByYearMonthClick = this.handleMergedFileByYearMonthClick.bind(this)
        this.handleMergedFileByWorkingCdClick = this.handleMergedFileByWorkingCdClick.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()

        try {
            await this.init()
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    private async init(): Promise<void> {
        const [assocs, accountingTermsDetail] = await Promise.all([
            this.getAssocInfo(),
            this.getAccountingTerms(),
        ])
        if (!accountingTermsDetail) {
            return
        }
        this.setState({
            assocs: assocs,
            accountingTermsDetail: accountingTermsDetail
        })

        const currentDateTime = await this.props.currentDateTimeRepo.getCurrentDateTime()
        if (!currentDateTime || !currentDateTime.details) {
            return
        }
        const currentYearMonthMoment = moment(currentDateTime.details.currentDateTime, 'YYYYMM')
        const currentMoment = moment(currentDateTime.details.currentDateTime)

        const selectedAccountingKey = this.getSelectedAccountingTermKey(
            accountingTermsDetail,
            currentYearMonthMoment
        )
        if (selectedAccountingKey !== null) {
            const assocWorkReportPlanResults =
                await this.getAssocWorkReportPlanResults(selectedAccountingKey)
            if (assocWorkReportPlanResults) {
                const selectedAccountingTerm = accountingTermsDetail.accountingTermInfo
                    .find(accountingTerm => accountingTerm.accountingTermKey === selectedAccountingKey)
                const fileSizeByYearMonthMap
                    = this.createFileSizeByYearMonthMap(
                        assocWorkReportPlanResults,
                        selectedAccountingTerm?.stopYearMonth,
                    )

                this.setState({
                    assocWorkReportPlanResultsDetail: assocWorkReportPlanResults,
                    fileSizeByYearMonthMap: fileSizeByYearMonthMap,
                })
            }
        } else {
            const nextTermReports = await this.getNextTermReports()
            if (nextTermReports) {
                this.setState({nextTermReports: nextTermReports})
            }
        }
        this.setState({
            currentMoment: currentMoment,
            selectedAccountingKey: selectedAccountingKey,
            selectMenuList: this.getAccountingTermSelectionList(accountingTermsDetail),
        })
    }

    async getAssocInfo(): Promise<Assoc[]> {
        const {condominiumCd, assocWorkReportRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            assocWorkReportRepo.getAssocs.bind(assocWorkReportRepo, condominiumCd),
            (httpErrorOrNetworkError) => {
                eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            },
        )
        return response?.details ? response.details.assocList : []
    }

    async getAccountingTerms(): Promise<Nullable<NatGetAccountingTermsDetail>> {
        const {condominiumCd, assocCd, assocWorkReportRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            assocWorkReportRepo.getAccountingTerms.bind(assocWorkReportRepo, condominiumCd, assocCd),
            (httpErrorOrNetworkError) => {
                eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            },
        )
        return response ? response.details : response
    }

    async getAssocWorkReportPlanResults(
        accountingTermKey: string,
    ): Promise<Nullable<NatGetAssocWorkReportPlanResultsDetail>> {
        const {condominiumCd, assocCd, assocWorkReportRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            assocWorkReportRepo.getAssocWorkReportPlanResults.bind(
                assocWorkReportRepo,
                condominiumCd,
                assocCd,
                accountingTermKey,
            ),
            (httpErrorOrNetworkError) => {
                eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            },
        )
        return response ? response.details : response
    }

    createFileSizeByYearMonthMap(
        assocWorkReportPlanResults: NatGetAssocWorkReportPlanResultsDetail,
        stopYearMonth?: string,
    ): Map<string, number> {
        const fileSizeByYearMonthMap = new Map<string, number>()
        const stopMoment = stopYearMonth ? moment(stopYearMonth, 'YYYYMM').startOf('month') : null

        assocWorkReportPlanResults.planResults.forEach(planResult => {
            planResult.businessInfo.forEach((businessInfo) => {
                businessInfo.report.forEach((report) => {
                    if (report.resultFiles.length === 0) {
                        return
                    }

                    if (!report.planResult && report.planExistence !== PLAN_EXISTENCE.HAVE.value) {
                        return
                    }

                    if (stopMoment) {
                        const targetMoment = moment(report.targetYearMonth, 'YYYYMM').startOf('month')
                        if (targetMoment.isSameOrAfter(stopMoment)) {
                            return
                        }
                    }

                    const mergedFileSizeKB = fileSizeByYearMonthMap.get(report.targetYearMonth) ?? 0
                    const resultFilesTotalSizeKB =
                        report.resultFiles.reduce((sum, str) => sum + Number(str.fileSizeKB), 0)

                    fileSizeByYearMonthMap.set(
                        report.targetYearMonth,
                        mergedFileSizeKB + resultFilesTotalSizeKB,
                    )
                })
            })
        })

        return fileSizeByYearMonthMap
    }

    async getNextTermReports(): Promise<Nullable<NatGetNextTermReportsDetail>> {
        const {condominiumCd, assocCd, assocWorkReportRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            assocWorkReportRepo.getNextTermReports.bind(assocWorkReportRepo, condominiumCd, assocCd),
            (httpErrorOrNetworkError) => {
                eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            },
        )
        return response ? response.details : response
    }

    /**
     * 初期選択される会計期キーを取得する処理
     * 返却する値は下記値を返却します。
     * ・会計期がない場合はNull
     * ・会計期がある場合、以下優先順で値を返却する。
     * ・現在年月を含む会計期がある:会計期キー > 翌期フラグがある:Null > 直近(過去)の会計期がある:会計期キー > 未来の会計期がある:会計期キー
     */
    getSelectedAccountingTermKey(
        accountingTermsDetail: NatGetAccountingTermsDetail,
        currentMoment: moment.Moment,
    ): Nullable<string> {
        if (accountingTermsDetail.accountingTermInfo.length === 0) {
            return null
        }
        const currentAccountingInfo = accountingTermsDetail.accountingTermInfo.find(
            (info) => {
                const startMoment = moment(info.startYearMonth, 'YYYYMM').startOf('month')
                const endMoment = moment(info.endYearMonth, 'YYYYMM').endOf('month')
                return currentMoment.isBetween(startMoment, endMoment, undefined, '[]')
            },
        )

        if (currentAccountingInfo !== undefined) {
            return currentAccountingInfo.accountingTermKey
        }

        if (accountingTermsDetail.nextTermReportFlag === HAVE_OR_NOT.HAVE.value) {
            return null
        }

        const recentAccountingKey = accountingTermsDetail.accountingTermInfo.find(
            (info) => {
                const startMoment = moment(info.startYearMonth, 'YYYYMM').startOf('month')
                return startMoment.isBefore(currentMoment, 'month')
            },
        )

        if (recentAccountingKey !== undefined) {
            return recentAccountingKey.accountingTermKey
        }

        const featureAccountingKey = accountingTermsDetail.accountingTermInfo.find(
            (info) => {
                const startMoment = moment(info.startYearMonth, 'YYYYMM').startOf('month')
                return startMoment.isAfter(currentMoment, 'month')
            },
        )

        if (featureAccountingKey !== undefined) {
            return featureAccountingKey.accountingTermKey
        }

        return null
    }

    getAccountingTermSelectionList(accountingTermsDetail: NatGetAccountingTermsDetail): SelectMenu[] {
        const selectMenuList: SelectMenu[] = accountingTermsDetail.accountingTermInfo.map((item) => {
            const startDate = convertToMoment(item.startYearMonth, null)
            const endDate = convertToMoment(item.endYearMonth, null)

            return {
                label: item.accountingTermName,
                additionalLabel: `${formatYearAndMonth(startDate)}～${formatYearAndMonth(endDate)}`,
                value: item.accountingTermKey,
            }
        })

        if (accountingTermsDetail.nextTermReportFlag === HAVE_OR_NOT.HAVE.value) {
            selectMenuList.unshift({
                label: '翌期報告書',
                additionalLabel: '',
                value: null,
            })
        }

        return selectMenuList
    }

    handleAccountingListOpen(event: React.MouseEvent<HTMLElement>): void {
        this.setState({accountingListOpen: true, accountingTermSelectBox: event.currentTarget})
    }

    handleAccountingListClose(): void {
        this.setState({accountingListOpen: false})
    }

    async handleAccountingTermChange(
        selectedValue: string | null,
    ): Promise<void> {
        const {eventEmitter} = this.props
        const {accountingTermsDetail} = this.state

        eventEmitter.emit('clearHeadingAreaMessage')
        await eventEmitter.showLoading()
        try {
            if (!selectedValue) {
                const nextTermReports = await this.getNextTermReports()
                if (nextTermReports) {
                    this.setState({nextTermReports: nextTermReports})
                }
            } else {
                const selectedAccountingTerm = accountingTermsDetail.accountingTermInfo
                    .find(accountingTerm => accountingTerm.accountingTermKey === selectedValue)
                if (selectedAccountingTerm) {
                    const assocWorkReportPlanResults =
                        await this.getAssocWorkReportPlanResults(selectedAccountingTerm.accountingTermKey)
                    if (assocWorkReportPlanResults) {
                        const fileSizeByYearMonthMap
                            = this.createFileSizeByYearMonthMap(
                            assocWorkReportPlanResults,
                            selectedAccountingTerm.stopYearMonth,
                        )

                        this.setState({
                            assocWorkReportPlanResultsDetail: assocWorkReportPlanResults,
                            fileSizeByYearMonthMap: fileSizeByYearMonthMap,
                        })
                    }
                }
            }
            this.setState({selectedAccountingKey: selectedValue})
        } finally {
            this.setState({accountingListOpen: false})
            await eventEmitter.closeLoading()
        }
    }

    handleDialogClose(): void {
        this.setState({dialogStatus: null})
    }

    handleBusinessNameClick(business: BusinessInfo): void {
        this.props.eventEmitter.emit('clearHeadingAreaMessage')
        this.setState({dialogStatus: 'business', confirmationBusinessInfo: business})
    }

    handleDownloadIconClick(targetYearMonth: string): void {
        this.props.eventEmitter.emit('clearHeadingAreaMessage')

        const totalFileSizeKB = this.state.fileSizeByYearMonthMap.get(targetYearMonth)
        if (!totalFileSizeKB) {
            return
        }
        this.setState({
            dialogStatus: 'yearMonth',
            confirmationYearMonthInfo: {
                targetYearMonth: targetYearMonth,
                totalFileSizeKB: totalFileSizeKB,
            }
        })
    }

    handleResultClick(businessName: string, resultFile: ResultFile[]): void {
        this.props.eventEmitter.emit('clearHeadingAreaMessage')
        this.setState({
            dialogStatus: 'resultFileList',
            confirmationResultInfo: {
                businessName: businessName,
                resultFiles: resultFile,
            },
        })
    }

    async handleMergedFileByYearMonthClick(targetYearMonth: string): Promise<void> {
        const {
            eventEmitter,
            asyncCallFunction,
            assocWorkReportRepo,
            condominiumCd,
            assocCd,
        } = this.props

        const {selectedAccountingKey} = this.state

        if (selectedAccountingKey === null) return

        await eventEmitter.showLoading()
        try {
            const fileNumber = await asyncCallFunction(
                // @ts-ignore
                assocWorkReportRepo.getMergedByYearMonthFileNumber.bind(
                    assocWorkReportRepo,
                    condominiumCd,
                    assocCd,
                    selectedAccountingKey,
                    targetYearMonth,
                ),
                (httpErrorOrNetworkError) => {
                    if (httpErrorOrNetworkError.responseBody.requestError.messageId === Messages.EREPR0003.id) {
                        this.init()
                    }
                    this.setState({
                        dialogStatus: null,
                    })
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                }
            )

            if (fileNumber == null) return

            const tempLink: HTMLAnchorElement = document.createElement('a')
            tempLink.href = `/api/common/file/ASSOC_WORK_REPORT_MERGED/${fileNumber}`
            document.body.appendChild(tempLink)
            tempLink.click()
        } finally {
            eventEmitter.closeLoading()
        }
    }

    async handleMergedFileByWorkingCdClick(targetWorkingCd: string): Promise<void> {
        const {
            eventEmitter,
            asyncCallFunction,
            assocWorkReportRepo,
            condominiumCd,
            assocCd,
        } = this.props

        const {selectedAccountingKey} = this.state

        if (selectedAccountingKey === null) return

        await eventEmitter.showLoading()
        try {
            const fileNumber = await asyncCallFunction(
                // @ts-ignore
                assocWorkReportRepo.getMergedByWorkingCdFileNumber.bind(
                    assocWorkReportRepo,
                    condominiumCd,
                    assocCd,
                    selectedAccountingKey,
                    targetWorkingCd,
                ),
                (httpErrorOrNetworkError) => {
                    if (httpErrorOrNetworkError.responseBody.requestError.messageId === Messages.EREPR0003.id) {
                        this.init()
                    }
                    this.setState({
                        dialogStatus: null,
                    })
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                }
            )

            if (fileNumber == null) return

            const tempLink: HTMLAnchorElement = document.createElement('a')
            tempLink.href = `/api/common/file/ASSOC_WORK_REPORT_MERGED/${fileNumber}`
            document.body.appendChild(tempLink)
            tempLink.click()
        } finally {
            eventEmitter.closeLoading()
        }
    }

    renderBreadcrumbList(): Nullable<ReactElement> {
        const {assocs} = this.state

        if (assocs.length === 1) {
            return null
        }

        const selectedAssoc = assocs.find(assoc => assoc.assocCd === this.props.assocCd)
        if (!selectedAssoc) {
            return null
        }

        const breadcrumbs = [
            <BreadcrumbListLinkButton
                onClick={() =>
                    this.props.navigate(
                        `/resident/condominiums/${this.props.condominiumCd}/assocs/selection`,
                    )}>
                管理組合選択
            </BreadcrumbListLinkButton>,
            <>
                {selectedAssoc.assocName}
            </>,
        ]
        return (
            <>
                <BreadcrumbList separator={<ForwardArrow/>} aria-label="breadcrumb">
                    {breadcrumbs}
                </BreadcrumbList>
                <Box height={'10px'}/>
            </>
        )
    }

    renderAccountingTerm(): ReactNode {
        const {accountingTermSelectBox, accountingListOpen, selectMenuList, selectedAccountingKey} = this.state

        const selectedItem = selectMenuList.find(select => select.value === selectedAccountingKey)
        if (!selectedItem) {
            return
        }

        return (
            <DisplayItemSelectWrapperExtendWidth>
                <AccountingTermSelect
                    onClick={this.handleAccountingListOpen}
                    aria-controls={'pp-accountingTerm-list'}
                >
                    <Box fontSize={FontSize.Accent} fontWeight={FontWeights.Bold} color={Colors.AccentDark}>
                        {selectedItem.label}
                    </Box>
                    <Box fontSize={FontSize.Small} color={Colors.AccentDark} pl={'15px'}>
                        {selectedItem.additionalLabel}
                    </Box>
                </AccountingTermSelect>
                <Menu
                    id={'pp-accountingTerm-list'}
                    anchorEl={accountingTermSelectBox}
                    keepMounted
                    open={accountingListOpen}
                    onClose={this.handleAccountingListClose}
                    // TODO: getContentAnchorElが削除されたため一旦削除
                    // getContentAnchorEl={null}
                    anchorOrigin={{vertical: 55, horizontal: 'left'}}
                    PaperProps={{style: {width: accountingTermSelectBox ? accountingTermSelectBox.clientWidth : 0}}}
                >
                    {
                        selectMenuList.map((selectMenu, key) => {
                            return (
                                <AccountingTermItem
                                    key={key}
                                    value={selectMenu.value ?? ''}
                                    selected={selectMenu.value === selectedAccountingKey}
                                    // onClick={event => this.handleAccountingTermChange(event, selectMenu.value)}
                                    onClick={() => this.handleAccountingTermChange(selectMenu.value)}
                                >
                                    {selectMenu.label}
                                    <Box fontSize={FontSize.Small} color={'#828282'} pl={'30px'}>
                                        {selectMenu.additionalLabel}
                                    </Box>
                                </AccountingTermItem>
                            )
                        })
                    }
                </Menu>
            </DisplayItemSelectWrapperExtendWidth>
        )
    }

    renderAssocWorkReport(): Nullable<ReactElement> {
        const {
            accountingTermsDetail,
            selectedAccountingKey,
            assocWorkReportPlanResultsDetail,
            fileSizeByYearMonthMap,
            nextTermReports
        } = this.state

        if (accountingTermsDetail.accountingTermInfo.length === 0 &&
            accountingTermsDetail.nextTermReportFlag === HAVE_OR_NOT.NOT.value) {
            return (
                <>
                    <Box height={'20px'}/>
                    <Box color={Colors.AccentDark}>
                        報告はありません。
                    </Box>
                </>
            )
        }

        if (!selectedAccountingKey) {
            return (
                <>
                    {this.renderAccountingTerm()}
                    <Box height={'15px'}/>
                    <NatNextTermReport resultFiles={nextTermReports.resultFiles}/>
                </>
            )
        }

        const selectedAccountingTerm = accountingTermsDetail.accountingTermInfo.find(
            (accountingTerm) => accountingTerm.accountingTermKey === selectedAccountingKey)
        if (selectedAccountingTerm) {
            return (
                <>
                    {this.renderAccountingTerm()}
                    <Box height={'15px'}/>
                    <MergeFeatureDescription>
                        ※ 当該月の報告書をまとめてダウンロードするにはダウンロードアイコン（ <FontAwesomeIcon
                        icon={faDownload}/> ）をクリックしてください。<br/>
                        ※ 報告項目ごとにまとめてダウンロードするには項目名をクリックしてください。
                    </MergeFeatureDescription>
                    <Box height={'5px'}/>
                    <TimeLineViewContainer>
                        <StickyDiv>
                            <NatAssocWorkReportTimeline
                                key={selectedAccountingTerm.accountingTermKey}
                                accountingTerm={{
                                    accountingTermKey: selectedAccountingTerm.accountingTermKey,
                                    startYearMonth: selectedAccountingTerm.startYearMonth,
                                    endYearMonth: selectedAccountingTerm.endYearMonth,
                                    stopYearMonth: selectedAccountingTerm.stopYearMonth,
                                }}
                                assocWorkReportPlanResultsDetail={assocWorkReportPlanResultsDetail}
                                mergedFileSizeByYearMonthMap={fileSizeByYearMonthMap}
                                maxWidth={{
                                    pc: DisplaySizeValue.ResidentWidthMax - (15 * 2),
                                    sp: BreakpointValues.md,
                                }}
                                onBusinessNameClick={this.handleBusinessNameClick}
                                onDownloadIconClick={this.handleDownloadIconClick}
                                onResultClick={this.handleResultClick}
                            />
                        </StickyDiv>
                    </TimeLineViewContainer>
                </>
            )
        }

        return null
    }

    render(): Nullable<ReactElement> {
        const {condominiumCd, assocCd, eventEmitter} = this.props

        const {
            currentMoment,
            accountingTermsDetail,
            isLoadCompleted,
            dialogStatus,
            selectedAccountingKey,
            confirmationBusinessInfo,
            confirmationYearMonthInfo,
            confirmationResultInfo
        } = this.state

        if (!isLoadCompleted || !currentMoment) {
            return null
        }

        const selectedAccountingTermName = accountingTermsDetail.accountingTermInfo.find(
            accountingTermInfo => accountingTermInfo.accountingTermKey === selectedAccountingKey
        )?.accountingTermName ?? '';

        const selectedAccountingTermStopYearMonth = accountingTermsDetail.accountingTermInfo.find(
            accountingTermInfo => accountingTermInfo.accountingTermKey === selectedAccountingKey
        )?.stopYearMonth

        return (
            <>
                {this.renderBreadcrumbList()}
                <RowFlexContainer>
                    <StyledPageTitle>管理事務報告</StyledPageTitle>
                </RowFlexContainer>
                <Box height={'20px'}/>
                <HeadingAreaMessageComponentForResident eventEmitter={eventEmitter}/>
                {this.renderAssocWorkReport()}
                <StyleBusinessResponsiveDialog
                    id={'pp-dialog'}
                    open={dialogStatus === 'business'}
                    transitionDuration={(process.env.NODE_ENV === 'test') ? 0 : undefined}
                    fullWidth
                >
                    <NatBusinessDetailDialog
                        currentMoment={currentMoment}
                        accountingTermName={selectedAccountingTermName}
                        businessName={confirmationBusinessInfo?.businessName ?? ''}
                        workingCd={confirmationBusinessInfo?.workingCd ?? ''}
                        stopYearMonth={selectedAccountingTermStopYearMonth}
                        implementationTimes={confirmationBusinessInfo?.implementationTimes}
                        businessType={confirmationBusinessInfo?.businessType}
                        implementationSubcontractor={confirmationBusinessInfo?.implementationSubcontractor}
                        publishInfo={confirmationBusinessInfo?.publishInfo}
                        report={confirmationBusinessInfo?.report}
                        onMergedFileByWorkingCdClick={this.handleMergedFileByWorkingCdClick}
                        onCancel={this.handleDialogClose}
                    />
                </StyleBusinessResponsiveDialog>
                <StyleResponsiveDialog
                    id={'pp-dialog'}
                    open={dialogStatus === 'yearMonth'}
                    transitionDuration={(process.env.NODE_ENV === 'test') ? 0 : undefined}
                    fullWidth
                >
                    <NatResidentMergedReportFileDialog
                        condominiumCd={condominiumCd}
                        assocCd={assocCd}
                        currentMoment={currentMoment}
                        targetYearMonth={confirmationYearMonthInfo?.targetYearMonth ?? ''}
                        totalFileSizeKB={confirmationYearMonthInfo?.totalFileSizeKB ?? 0}
                        onMergedFileByYearMonthClick={this.handleMergedFileByYearMonthClick}
                        onClose={this.handleDialogClose}
                    />
                </StyleResponsiveDialog>
                <StyleResponsiveDialog
                    id={'pp-dialog'}
                    open={dialogStatus === 'resultFileList'}
                    transitionDuration={(process.env.NODE_ENV === 'test') ? 0 : undefined}
                    fullWidth
                >
                    <NatResidentReportFileListDialog
                        onClose={this.handleDialogClose}
                        businessName={confirmationResultInfo?.businessName ?? ''}
                        resultFiles={confirmationResultInfo?.resultFiles ?? []}
                    />
                </StyleResponsiveDialog>
            </>
        )
    }
}

export default WithWidth(createResidentWithErrorHandlingPage(NatResidentAssocWorkReportPage))

const BreadcrumbList = withStyles({
    separator: {
        marginLeft: '0px',
        marginRight: '5px',
    },
    li: {
        fontSize: FontSize.Small,
        lineHeight: '18px',
        fontFamily: 'Noto Sans JP,sans-serif',
        color: Colors.Body,
        '&:last-child': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
        },
    },
})(Breadcrumbs)

const BreadcrumbListLinkButton = styled(PlainLinkStyledButton)`
  font-size: ${FontSize.Small};
`

const ForwardArrow = styled.div`
  content: '';
  width: 0.9rem;
  height: 0.9rem;
  border-top: solid 1px #BDBDBD;
  border-right: solid 1px #BDBDBD;
  -webkit-transform: rotate(45deg);
  transform: rotate(45deg);
  margin-left: auto;
`

const AccountingTermItem = withStyles({
    root: {
        fontSize: FontSize.Middle,
        fontFamily: '"Noto Sans JP", sans-serif',
        color: Colors.Body,
        backgroundColor: 'transparent',
        '&:focus': {
            backgroundColor: '#D9D9D980',
        },
        '&:hover': {
            backgroundColor: '#F8F8F8',
        },
    },
    selected: {
        backgroundColor: '#0070B033!important',
    },
})(MenuItem)

const AccountingTermSelect = styled(DisplayItemSelectLikeDiv)`
  border: none;
`

const MergeFeatureDescription = styled.div`
  font-size: ${FontSize.Middle};
`

const TimeLineViewContainer = styled.div`
  background-color: #FFFFFF;
  ${currentMedia.greaterThan('pc')`
      max-width: ${DisplaySizeValue.ResidentWidthMax - (15 * 2)}px;
      margin: auto;
  `}
  ${currentMedia.lessThan('sp')`
      margin-left: -15px;
      margin-right: -15px;
  `}
`

const StyleResponsiveDialog = styled(ResponsiveDialog)`
  ${currentMedia.greaterThan('pc')`
     & .MuiDialog-paper {
         width: 477px;
         min-height: 312px;
     }
  `}
`

const StyleBusinessResponsiveDialog = styled(ResponsiveDialog)`
  ${currentMedia.greaterThan('pc')`
     & .MuiDialog-paper {
         width: 475px;
     }
  `}
`

const DisplayItemSelectWrapperExtendWidth = styled(DisplayItemSelectWrapper)`
  ${currentMedia.greaterThan('pc')`
     width: 520px;
  `}
`
